/**
 * Delivering the export document.
 *
 * Both routes are client-side, matching the legacy tool. PDF goes through the
 * browser's own print dialog rather than a server-side renderer: it needs no
 * headless Chromium in the image, and the document never leaves the machine it
 * was produced on — which matters for something reproducing Microsoft
 * Confidential material.
 */

/**
 * Byte-order mark, so Word reads the file as UTF-8 rather than guessing a
 * codepage and turning every em dash into mojibake. Written as an escape
 * because a literal BOM in source is invisible.
 */
const UTF8_BOM = '\uFEFF';

/**
 * Opens the print dialog on the export document.
 *
 * Rendered in a detached iframe rather than a new window so a popup blocker
 * cannot silently swallow it, and so the calculator behind it is untouched.
 * Resolves false when printing was refused, which the caller surfaces — a
 * silently missing PDF is worse than being told to use Word instead.
 */
export async function printExport(html: string): Promise<boolean> {
  const frame = document.createElement('iframe');
  frame.setAttribute('aria-hidden', 'true');
  frame.setAttribute('title', 'Export preview');
  // Off-screen rather than display:none — a hidden iframe does not always get a
  // layout, and Chrome will print a blank page for one that never laid out.
  frame.style.cssText =
    'position:fixed;left:-10000px;top:0;width:820px;height:1160px;border:0;visibility:hidden;';

  // `srcdoc` rather than document.write: same self-contained string, no
  // deprecated API, and the load event gives a definite point at which the
  // document has laid out and is safe to print.
  frame.srcdoc = html;

  const printed = await new Promise<boolean>((resolve) => {
    let settled = false;
    const done = (value: boolean): void => {
      if (settled) return;
      settled = true;
      resolve(value);
    };

    frame.addEventListener('load', () => {
      const win = frame.contentWindow;
      if (!win) {
        done(false);
        return;
      }
      try {
        win.focus();
        win.print();
        done(true);
      } catch {
        done(false);
      }
    });

    frame.addEventListener('error', () => {
      done(false);
    });

    document.body.appendChild(frame);

    // A frame that never fires load — a blocked or hung renderer — must not
    // leave the caller waiting forever with no feedback.
    window.setTimeout(() => {
      done(false);
    }, 10_000);
  });

  // Left in place briefly: removing the frame in the same tick can cancel the
  // print job in browsers that are still reading from it.
  window.setTimeout(() => {
    frame.remove();
  }, 60_000);

  return printed;
}

/**
 * Downloads the export as a Word document.
 *
 * A `.doc` that is really HTML, wrapped in the MSO namespaces and a
 * `w:WordDocument` print-view block. Not a real `.docx` — this is what the legacy
 * tool produced, it opens cleanly in Word and LibreOffice, and it keeps the whole
 * thing a single self-contained string rather than pulling in a docx builder.
 */
export function downloadWordExport(html: string, fileName: string): void {
  const wrapped = html.replace(
    '<html lang="en-AU">',
    `<html lang="en-AU" xmlns:o="urn:schemas-microsoft-com:office:office" xmlns:w="urn:schemas-microsoft-com:office:word" xmlns="http://www.w3.org/TR/REC-html40">
<!--[if gte mso 9]><xml><w:WordDocument><w:View>Print</w:View><w:Zoom>100</w:Zoom>
<w:DoNotOptimizeForBrowser/></w:WordDocument></xml><![endif]-->`,
  );

  const blob = new Blob([UTF8_BOM + wrapped], { type: 'application/msword;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = fileName;
  document.body.appendChild(link);
  link.click();
  link.remove();
  // Revoked on a later tick: revoking immediately races the download starting.
  window.setTimeout(() => {
    URL.revokeObjectURL(url);
  }, 10_000);
}
